import { useEffect, useState, useCallback, useRef } from 'react'
import { useBlocker } from 'react-router-dom'
import type { UseFormReturn } from 'react-hook-form'
import { BLOCKER_STATE } from '@/constants/hook'

interface UseUnsavedChangesOptions {
  onBeforeUnload?: boolean
  enabled?: boolean
}

interface UseUnsavedChangesReturn {
  showDialog: boolean
  handleConfirm: () => void
  handleCancel: () => void
  allowNavigation: () => void
  isDirty: boolean
}

export function useUnsavedChanges<TFieldValues extends Record<string, unknown>>(
  form: UseFormReturn<TFieldValues>,
  options: UseUnsavedChangesOptions = {}
): UseUnsavedChangesReturn {
  const { onBeforeUnload = true, enabled = true } = options

  const [showDialog, setShowDialog] = useState(false)
  const shouldAllowNavigation = useRef(false)
  const isClosing = useRef(false)

  const allowNavigation = useCallback(() => {
    shouldAllowNavigation.current = true
  }, [])

  const isDirty = Object.keys(form.formState.dirtyFields).length > 0

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    const formIsDirty = Object.keys(form.formState.dirtyFields).length > 0

    if (!enabled) return false

    if (shouldAllowNavigation.current) return false

    if (form.formState.isSubmitSuccessful) return false

    if (!formIsDirty) return false

    return currentLocation.pathname !== nextLocation.pathname
  })

  useEffect(() => {
    if (
      blocker.state === BLOCKER_STATE.BLOCKED &&
      !shouldAllowNavigation.current &&
      !isClosing.current
    ) {
      setShowDialog(true)
    }

    if (blocker.state === BLOCKER_STATE.UNBLOCKED) {
      isClosing.current = false
    }
  }, [blocker.state, showDialog])

  useEffect(() => {
    if (!onBeforeUnload || !enabled) return

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty && !shouldAllowNavigation.current) {
        e.preventDefault()
        e.returnValue = ''
        return ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty, onBeforeUnload, enabled])

  const handleConfirm = useCallback(() => {
    isClosing.current = true
    shouldAllowNavigation.current = true
    setShowDialog(false)
    setTimeout(() => {
      blocker.proceed?.()
    }, 0)
  }, [blocker])

  const handleCancel = useCallback(() => {
    isClosing.current = true
    setShowDialog(false)
    setTimeout(() => {
      blocker.reset?.()
    }, 0)
  }, [blocker])

  useEffect(() => {
    if (!isDirty) {
      shouldAllowNavigation.current = false
    }
  }, [isDirty])

  return {
    showDialog,
    handleConfirm,
    handleCancel,
    allowNavigation,
    isDirty,
  }
}
